"use client";

import { cn } from "@dafiti/closet-ui/utils";

import { useFilterActions } from "../../../hooks/useFilterActions.hook";
import { useQuickFilters } from "../../../hooks/useQuickFilters.hook";
import type { CatalogMetaResponse } from "../../../types/catalog.type";
import { BooleanQuickFilter } from "../../Filters/BooleanQuickFilter";
import { MoreFiltersDrawer } from "../../Filters/MoreFiltersDrawer";
import { QuickFilterTrigger } from "../../Filters/QuickFilterTrigger";
import { SortControl } from "../../Filters/SortControl";
import { QuickFiltersBarSkeleton } from "./QuickFiltersBarSkeleton";

type QuickFiltersBarProps = {
  meta: CatalogMetaResponse;
};

export function QuickFiltersBar({ meta }: QuickFiltersBarProps) {
  const { booleanFilters, dropdownFilters, isLoading } = useQuickFilters();
  const { getActiveFilterFields } = useFilterActions();
  const hasActiveFilters = getActiveFilterFields().length > 0;

  if (isLoading) return <QuickFiltersBarSkeleton />;

  return (
    <div
      className={cn(
        "grid items-center gap-0 lg:gap-y-4",
        // Mobile: [filters][divider][sorting][scroll-list] | row2: [booleans full]
        "grid-cols-[auto_auto_auto_1fr]",
        "[grid-template-areas:'filters_sorting_divider_list''hdivider_hdivider_hdivider_hdivider''booleans_booleans_booleans_booleans']",
        // lg: row1=[booleans, sorting right] | row2=[list full width + filters inside]
        "lg:grid-cols-[1fr_auto]",
        "lg:[grid-template-areas:'booleans_sorting''list_list']",
        // xl: row1=[booleans full] | row2=[list + filters inside, sorting far right]
        "xl:[grid-template-areas:'booleans_booleans''list_sorting']",
      )}
      data-testid="quick-filters-bar"
    >
      <div className="[grid-area:filters] pb-2 lg:pb-0 flex items-center mr-4 lg:hidden">
        <MoreFiltersDrawer
          hasActiveFilters={hasActiveFilters}
          variant="mobile"
        />
      </div>

      <div className="[grid-area:divider] mb-2 lg:pb-0 lg:hidden self-center w-px h-8 bg-gray-200 ml-4 mr-2" />

      <div className="[grid-area:sorting] pb-2 lg:pb-0 flex items-center lg:justify-self-end">
        <SortControl sorting={meta.header.sorting} />
      </div>

      <div className="[grid-area:hdivider] lg:hidden w-full h-px bg-gray-200 mb-4" />

      <div className="[grid-area:list] pb-2 lg:pb-0 flex items-center overflow-x-auto lg:overflow-x-visible scrollbar-none gap-2">
        {dropdownFilters.map((facet, index) => (
          <QuickFilterTrigger
            key={facet.field}
            facet={facet}
            boxAlign={index === 0 ? "start" : "center"}
          />
        ))}
        <div className="hidden lg:flex shrink-0">
          <MoreFiltersDrawer
            hasActiveFilters={hasActiveFilters}
            variant="desktop"
          />
        </div>
      </div>

      {booleanFilters.length > 0 && (
        <div className="[grid-area:booleans] flex items-center gap-2 lg:gap-3xs">
          {booleanFilters.map((facet) => (
            <BooleanQuickFilter key={facet.field} facet={facet} />
          ))}
        </div>
      )}
    </div>
  );
}
